import { useEffect, useRef, useState, useMemo } from 'react';
import { ChevronDown, Search, RefreshCw, X, CheckCircle2 } from 'lucide-react';

/**
 * QboPicker — searchable dropdown of QBO accounts or classes.
 *
 * Behavior (per MEC_REQUIREMENTS.md §5):
 *   - Fetches LIVE from the backend every time the user opens the dropdown.
 *     No client-side caching: if the bookkeeper just added an account in QBO
 *     it must show up after Refresh without a full page reload.
 *   - Display order: `{number} - {name}` if number, else `{fully_qualified_name}` or `{name}`.
 *   - In-list search filter (no external library).
 *   - Refresh button re-runs the fetch.
 *   - Loading + error + empty states all rendered inline.
 *
 * Controlled component: parent owns the selected id+name. We never write
 * to the backend from here — selection is reported via onChange.
 *
 * Props:
 *   value: { id, name } | null          — current selection
 *   onChange: ({ id, name }) => void     — fired when user picks an option
 *   onClear: () => void                  — fired when user clears
 *   fetchOptions: () => Promise<{ options: Array<{ id, label, sublabel }> }>
 *                                         normalized so the picker doesn't
 *                                         have to know "accounts vs classes"
 *   placeholder: string
 *   disabled: boolean
 *   emptyHint: string                    — shown when no QBO data returns
 */
export default function QboPicker({
  value,
  onChange,
  onClear,
  fetchOptions,
  placeholder = 'Select…',
  disabled = false,
  emptyHint = 'No items returned from QuickBooks.',
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState(null); // null = never fetched; [] = fetched empty
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Auto-focus the search box when opening
  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchOptions();
      setOptions(result?.options || []);
    } catch (err) {
      setError(err?.message || 'Failed to load from QuickBooks');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // Open + fetch fresh on click (always live — no cache).
  const openAndFetch = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    load();
  };

  // Refresh re-runs the live call without closing the dropdown.
  const onRefresh = (e) => {
    e.stopPropagation();
    load();
  };

  const filtered = useMemo(() => {
    if (!options) return [];
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel || '').toLowerCase().includes(q)
    );
  }, [options, query]);

  const displayLabel = value?.name || placeholder;

  return (
    <div ref={wrapRef} className="relative w-full">
      {/* Trigger */}
      <button
        type="button"
        onClick={openAndFetch}
        disabled={disabled}
        className={`
          group w-full h-9 px-3 flex items-center gap-2
          bg-[var(--color-cream-50)]
          border ${value ? 'border-[var(--color-warm-200)]' : 'border-dashed border-[var(--color-warm-300)]'}
          rounded-[var(--radius-md)]
          text-[13px] text-left
          ${value ? 'text-[var(--color-warm-900)]' : 'text-[var(--color-warm-500)]'}
          hover:border-[var(--color-warm-400)]
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-colors
        `}
        title={value ? value.name : ''}
      >
        <span className="flex-1 min-w-0 truncate">{displayLabel}</span>
        {value && !disabled && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onClear?.();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onClear?.();
              }
            }}
            className="shrink-0 inline-flex p-0.5 rounded text-[var(--color-warm-400)] hover:text-[var(--color-danger-600)] hover:bg-[var(--color-danger-50)] cursor-pointer"
            aria-label="Clear selection"
          >
            <X size={12} />
          </span>
        )}
        <ChevronDown
          size={14}
          className={`shrink-0 text-[var(--color-warm-500)] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          className="absolute z-30 mt-1 w-[320px] max-w-[90vw] bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] shadow-[var(--shadow-lg)] overflow-hidden animate-slide-up"
        >
          {/* Search row + refresh */}
          <div className="flex items-center gap-1.5 p-2 border-b border-[var(--color-warm-100)]">
            <div className="relative flex-1">
              <Search
                size={12}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-warm-500)] pointer-events-none"
              />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter…"
                className="w-full h-8 pl-7 pr-2 bg-[var(--color-cream-100)] border border-transparent focus:border-[var(--color-insight-500)] focus:bg-[var(--color-cream-50)] focus:outline-none rounded-[var(--radius-sm)] text-[12px] text-[var(--color-warm-900)] placeholder:text-[var(--color-warm-400)]"
              />
            </div>
            <button
              type="button"
              onClick={onRefresh}
              title="Re-fetch from QuickBooks"
              className="shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-warm-500)] hover:bg-[var(--color-cream-200)] hover:text-[var(--color-warm-900)] transition-colors"
              aria-label="Refresh from QuickBooks"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          {/* List body */}
          <div className="max-h-72 overflow-y-auto py-1">
            {loading && (
              <div className="flex items-center gap-2 px-3 py-3 text-[12px] text-[var(--color-warm-500)]">
                <span className="inline-block w-3 h-3 border-2 border-[var(--color-warm-300)] border-t-[var(--color-warm-700)] rounded-full animate-spin" />
                Loading from QuickBooks…
              </div>
            )}

            {!loading && error && (
              <div className="px-3 py-3 text-[12px] text-[var(--color-danger-700)] bg-[var(--color-danger-50)] mx-2 my-1 rounded-[var(--radius-sm)]">
                {error}
              </div>
            )}

            {!loading && !error && options !== null && filtered.length === 0 && (
              <div className="px-3 py-4 text-[12px] text-[var(--color-warm-500)]">
                {query ? 'No matches for your filter.' : emptyHint}
              </div>
            )}

            {!loading && !error && filtered.length > 0 && (
              <ul>
                {filtered.map((opt) => {
                  const selected = value?.id === opt.id;
                  return (
                    <li key={opt.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onChange({ id: opt.id, name: opt.label });
                          setOpen(false);
                        }}
                        className={`
                          w-full text-left px-3 py-2 flex items-start gap-2 text-[12px]
                          hover:bg-[var(--color-cream-200)] transition-colors
                          ${selected ? 'bg-[var(--color-cream-100)]' : ''}
                        `}
                      >
                        <span className="flex-1 min-w-0">
                          <span className="block truncate text-[var(--color-warm-900)] font-medium">
                            {opt.label}
                          </span>
                          {opt.sublabel && (
                            <span className="block truncate text-[11px] text-[var(--color-warm-500)] mt-0.5">
                              {opt.sublabel}
                            </span>
                          )}
                        </span>
                        {selected && (
                          <CheckCircle2
                            size={14}
                            className="shrink-0 mt-0.5 text-[var(--color-recovery-500)]"
                          />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Helper: turn a QBO account row into the picker's normalized shape.
 * Used by the Account Routing page when wiring up the fetcher.
 */
export function accountToOption(a) {
  const number = (a.number || '').toString().trim();
  const name = a.name || a.fully_qualified_name || '(unnamed)';
  const label = number ? `${number} - ${name}` : (a.fully_qualified_name || name);
  const sublabel = [a.account_type, a.account_subtype].filter(Boolean).join(' · ');
  return { id: String(a.id), label, sublabel };
}

/**
 * Helper: turn a QBO class row into the picker's normalized shape.
 */
export function classToOption(c) {
  return {
    id: String(c.id),
    label: c.fully_qualified_name || c.name,
    sublabel: null,
  };
}
